import React, { useState, useRef, useEffect } from 'react'
import { FormattedMessage, useIntl, FormattedDate } from 'react-intl'
import messages from './messages'
import Icon from 'components/Icon'
import { useDrag, useDrop } from 'react-dnd'
import DemoInlineEditor from '../DemoInlineEditor'
import DemoPlayer from '../DemoPlayer'
import { Chip, Button } from '@arnsbogroup/react-voicearchive'

const Demo = ({ demo: initialDemo, supplierId, index, onMove, onDelete, onToggle, loading = false }) => {
  const { formatMessage } = useIntl()
  const [demo, setDemo] = useState(initialDemo)
  const [editing, setEditing] = useState(false)

  const { created_at, uid, cdn_url, type, product, voicekind, voiceage, language, active, name, supplierPromoPriorityIds, dialect } = demo

  const onClick = (event) => {
    event.stopPropagation()

    if (onDelete) {
      onDelete(uid)
    }
  }

  const onToggleDemo = (event) => {
    event.stopPropagation()

    if (onToggle) {
      onToggle(initialDemo)
    }
  }

  const ref = useRef(null)

  const [, drop] = useDrop({
    accept: 'DEMO',
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return // Don't replace items with themselves
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      onMove(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    item: { type: 'DEMO', uid, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1

  drag(drop(ref))

  return (
    <div ref={ref} className="w-full">
      { editing ? (
        <DemoInlineEditor
          ref={ref}
          supplierId={supplierId}
          demo={demo}
          onCancel={() => setEditing(false)}
          onChange={(nextDemo) => {
            setDemo({ ...demo, ...nextDemo })
            setEditing(false)
          }}
        />
      ) : (
        <div className="bg-white border border-gray px-4 py-3 flex items-center space-x-2" style={{ opacity }}>
          <div className="w-1/12">
            <DemoPlayer src={cdn_url} />
          </div>
          <div className="w-8/12 truncate text-base">
            <div className="text-gray-900 font-medium truncate" title={`#${uid}`}>
              { name || product || type || `#${uid}`|| supplierPromoPriorityIds || dialect }
              
                { type &&
                  <FormattedMessage {...messages.customDemoHint }>
                    { (hint) => (
                      <Chip
                        size="xs"
                        title={hint}
                        onClick={() => window.alert(hint)} 
                        className="ml-2">
                          <FormattedMessage {...messages.customDemoLabel } />
                      </Chip>
                    ) }
                  </FormattedMessage>
                }
                { supplierPromoPriorityIds && supplierPromoPriorityIds.length > 0 &&
                  <FormattedMessage {...messages.voiceLibraryHint }>
                    { (vlhint) => (
                      <Chip
                        size="xs"
                        title={vlhint}
                        onClick={() => window.alert(vlhint)} 
                        className="ml-2">
                          <FormattedMessage {...messages.voiceLibraryLabel } />
                      </Chip>
                    ) }
                  </FormattedMessage>
                }
                { dialect &&
                  <FormattedMessage 
                    {...messages.dialectHint }
                    values={{
                      dialect: dialect,
                    }}
                  >
                    { (dialectHint) => (
                      <Chip
                        size="xs"
                        title={dialectHint}
                        onClick={() => window.alert(dialectHint)} 
                        className="ml-2">
                          <FormattedMessage
                            {...messages.dialectLabel }
                          />
                      </Chip>
                    ) }
                  </FormattedMessage>
                }
            </div>
            <div className="text-gray-700 text-xs flex space-x-2">
              <div>{ product || type }{ language && language !== '' && `, ${language}` }{ dialect && `, ${dialect}` } #{ uid }</div>
              { created_at &&
                <div>
                  <Icon icon={['fal', 'clock']} className="mr-1" />
                  <FormattedDate value={created_at} />
                </div>
              }
            </div>
          </div>
          <div className="w-3/12 flex justify-between items-center text-sm">
            <FormattedMessage {...messages[active ? 'showLabel' : 'hideLabel']}>
              { (title) => (
                <div className={`cursor-pointer rounded-full w-2 h-2 ${active ? 'bg-green-400' : 'bg-red-400'}`} title={title} />
              ) }
            </FormattedMessage>

            { onToggle &&
              <FormattedMessage {...messages[active ? 'hideTitle' : 'showTitle']}>
                { (title) => (
                  <Button 
                    variant="inline"
                    size='xs'
                    onClick={onToggleDemo} 
                    title={title}
                  >
                    <FormattedMessage {...messages[active ? 'hide' : 'show']} />
                  </Button>
                ) }
              </FormattedMessage>
            }

            <a className="text-primary" href={`/download/supplier/demo/${uid}`} target="_blank">
              <Button 
                variant="inline"
                size='xs'
              >
                <FormattedMessage {...messages.download} />
              </Button>
            </a>

            <Button
              variant="inline"
              size='xs'
              onClick={() => setEditing(true)}
            >
              <FormattedMessage {...messages.edit} />
            </Button>

            <Button 
              variant="inline"
              size='xs'
              state="negative"
              onClick={ onDelete ? onClick : undefined }
              disabled={ !onDelete }
            >
              <FormattedMessage {...messages.delete} />
            </Button>

            <Icon icon={['fas', 'sort']} className="cursor-pointer text-gray-400" />
          </div>
        </div>
      ) }
    </div>
  )
}

export default Demo
